// src/components/eventos/views/EventosPalestrante.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';

/* ---- Ícones SVG ---- */
const ArrowLeftIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);
const MicIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
    </svg>
);
const ExpandIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
    </svg>
);
const CompressIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" /><line x1="10" y1="14" x2="3" y2="21" /><line x1="21" y1="3" x2="14" y2="10" />
    </svg>
);
const ClockIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
);
const BellIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
);
const LogoutIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);
const ChevronIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const formatarTempo = (segundos) => {
    const mins = Math.floor(Math.abs(segundos) / 60);
    const secs = Math.abs(segundos) % 60;
    const sinal = segundos < 0 ? '-' : '';
    return `${sinal}${mins.toString().padStart(2,'0')}:${secs.toString().padStart(2,'0')}`;
};

const EventosPalestrante = ({ supabase, session, clienteData, onLogout }) => {
    const navigate = useNavigate();
    const toast = useToast();
    const toastRef = useRef(toast);

    const [eventoAtivo, setEventoAtivo] = useState(null);
    const [tarefaAtual, setTarefaAtual] = useState(null);
    const [mensagens, setMensagens] = useState([]);
    const [mensagensNaoLidas, setMensagensNaoLidas] = useState([]);
    const [tempoAtual, setTempoAtual] = useState(new Date());
    const [isLoading, setIsLoading] = useState(true);
    const [modoApresentacao, setModoApresentacao] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const mensagensNotificadasRef = useRef(new Set());

    const nomeUsuario = session?.user?.user_metadata?.nome || 'Usuário';
    const iniciais = nomeUsuario.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

    useEffect(() => { toastRef.current = toast; }, [toast]);

    const handleLogout = async () => { try { await onLogout(); } catch (e) { console.error(e); } };

    const toggleModoApresentacao = () => {
        if (!modoApresentacao) {
            const elem = document.documentElement;
            if (elem.requestFullscreen) elem.requestFullscreen();
            else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
            else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
        } else {
            if (document.exitFullscreen) document.exitFullscreen();
            else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
            else if (document.msExitFullscreen) document.msExitFullscreen();
        }
        setModoApresentacao(!modoApresentacao);
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement && !document.webkitFullscreenElement && !document.msFullscreenElement) setModoApresentacao(false);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    useEffect(() => {
        const timer = setInterval(() => setTempoAtual(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const fetchEventoAtivo = useCallback(async () => {
        if (!session) return;
        try {
            const clienteId = clienteData?.id || session.user.user_metadata?.cliente_id;
            let query = supabase.from('eventos').select('*').eq('status', 'em_andamento').order('created_at', { ascending: false }).limit(1);
            if (clienteId) query = query.eq('cliente_id', clienteId);
            const { data: eventos, error } = await query;
            if (error) throw error;
            if (eventos && eventos.length > 0) {
                setEventoAtivo(eventos[0]);
                const tarefaEmAndamento = eventos[0].tarefas?.find(t => t.status === 'em_andamento');
                setTarefaAtual(tarefaEmAndamento || null);
            } else {
                setEventoAtivo(null);
                setTarefaAtual(null);
            }
        } catch (error) { console.error('Erro ao buscar evento ativo:', error); }
        finally { setIsLoading(false); }
    }, [supabase, session, clienteData]);

    const fetchMensagens = useCallback(async () => {
        if (!eventoAtivo || !clienteData) return;
        try {
            const { data, error } = await supabase.from('mensagens_evento').select('*').eq('cliente_id', clienteData.id).eq('evento_id', eventoAtivo.id).eq('lida', false).order('created_at', { ascending: false }).limit(10);
            if (error) throw error;
            const novasMensagens = data || [];
            setMensagens(novasMensagens);
            setMensagensNaoLidas(novasMensagens);
            const novasMensagensParaNotificar = novasMensagens.filter(msg => !mensagensNotificadasRef.current.has(msg.id));
            if (novasMensagensParaNotificar.length > 0) {
                novasMensagensParaNotificar.forEach(msg => {
                    mensagensNotificadasRef.current.add(msg.id);
                    toastRef.current({ title: 'Nova mensagem do diretor', description: msg.mensagem, status: 'info', duration: 8000, isClosable: true, position: 'top-right' });
                });
            }
        } catch (error) { console.error('Erro ao buscar mensagens:', error); }
    }, [supabase, eventoAtivo, clienteData]);

    useEffect(() => { fetchEventoAtivo(); const interval = setInterval(fetchEventoAtivo, 5000); return () => clearInterval(interval); }, [fetchEventoAtivo]);
    useEffect(() => { if (eventoAtivo) { fetchMensagens(); const interval = setInterval(fetchMensagens, 3000); return () => clearInterval(interval); } }, [eventoAtivo, fetchMensagens]);

    useEffect(() => {
        if (!menuOpen) return;
        const close = () => setMenuOpen(false);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [menuOpen]);

    const calcularTempoRestante = () => {
        if (!tarefaAtual) return null;
        const agora = tempoAtual;
        const [h, m] = (tarefaAtual.horaInicioReal || tarefaAtual.horaInicio).split(':').map(Number);
        const inicio = new Date(agora);
        inicio.setHours(h, m, 0);
        const decorrido = Math.floor((agora - inicio) / 1000);
        const duracao = tarefaAtual.duracao * 60;
        const restante = duracao - decorrido;
        const progresso = Math.min(100, Math.max(0, (decorrido / duracao) * 100));
        return { restante, progresso, atrasado: restante < 0 };
    };

    const tempoInfo = calcularTempoRestante();

    if (isLoading) {
        return (
            <div style={{ minHeight:'100vh', background:'#f0faf6', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Inter', -apple-system, sans-serif" }}>
                <div style={{ textAlign:'center' }}>
                    <div style={{ width:'48px', height:'48px', borderRadius:'50%', border:'3px solid rgba(44,203,144,0.2)', borderTopColor:'#2CCB90', animation:'spin 0.8s linear infinite', margin:'0 auto 16px' }} />
                    <p style={{ color:'#94a3b8', fontSize:'14px', margin:0 }}>Carregando...</p>
                </div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // MODO APRESENTAÇÃO
    if (modoApresentacao && eventoAtivo && tarefaAtual) {
        const bg = tempoInfo?.atrasado ? '#1a0a0a' : '#040d0a';
        const accentColor = tempoInfo?.atrasado ? '#f87171' : '#2CCB90';
        const accentBg = tempoInfo?.atrasado ? 'rgba(239,68,68,0.15)' : 'rgba(44,203,144,0.12)';
        const accentBorder = tempoInfo?.atrasado ? 'rgba(239,68,68,0.3)' : 'rgba(44,203,144,0.25)';

        return (
            <div style={{ minHeight:'100vh', background: bg, display:'flex', flexDirection:'column', padding:'32px', fontFamily:"'Inter', -apple-system, sans-serif" }}>
                {/* Header */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'40px' }}>
                    <div style={{ display:'inline-flex', alignItems:'center', background:'rgba(255,255,255,0.95)', borderRadius:'10px', padding:'6px 14px' }}>
                        <img src="/logo-horizontal.png" alt="Evlo" style={{ height:'28px', objectFit:'contain' }} onError={e => { e.target.style.display='none'; }} />
                    </div>
                    <button onClick={toggleModoApresentacao} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 18px', borderRadius:'10px', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', color:'rgba(148,182,220,0.8)', fontSize:'14px', cursor:'pointer', fontFamily:'inherit', fontWeight:600 }}>
                        <CompressIcon /> Sair da Apresentação
                    </button>
                </div>

                {/* Conteúdo centralizado */}
                <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', maxWidth:'1200px', margin:'0 auto', width:'100%', gap:'36px' }}>

                    {/* Status + Título */}
                    <div style={{ textAlign:'center' }}>
                        <span style={{ display:'inline-flex', padding:'6px 20px', borderRadius:'100px', background: accentBg, border:`1px solid ${accentBorder}`, color: accentColor, fontSize:'16px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'16px' }}>
                            {tempoInfo?.atrasado ? 'Tempo Excedido' : 'No Prazo'}
                        </span>
                        <h1 style={{ margin:0, fontSize:'clamp(2rem, 5vw, 4rem)', fontWeight:800, color:'#fff', letterSpacing:'-0.02em' }}>
                            {tarefaAtual.titulo}
                        </h1>
                    </div>

                    {/* Cronômetro gigante */}
                    <div style={{ background:'rgba(255,255,255,0.05)', border:`2px solid ${accentBorder}`, borderRadius:'24px', padding:'48px 80px', textAlign:'center', boxShadow:`0 0 60px ${accentBg}` }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'12px', marginBottom:'12px' }}>
                            <span style={{ color: accentColor }}><ClockIcon /></span>
                            <span style={{ fontSize:'16px', color:'rgba(148,182,220,0.7)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.1em' }}>
                                Tempo {tempoInfo?.atrasado ? 'Extra' : 'Restante'}
                            </span>
                        </div>
                        <div style={{ fontSize:'clamp(6rem, 15vw, 12rem)', fontWeight:800, color: accentColor, lineHeight:1, fontFamily:'monospace', letterSpacing:'-0.02em' }}>
                            {formatarTempo(tempoInfo?.restante || 0)}
                        </div>
                        {/* Barra de progresso */}
                        <div style={{ marginTop:'24px', height:'12px', borderRadius:'6px', background:'rgba(255,255,255,0.08)', overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${tempoInfo?.progresso || 0}%`, background: tempoInfo?.atrasado ? 'linear-gradient(90deg, #f87171, #ef4444)' : 'linear-gradient(90deg, #2CCB90, #2DB8E1)', borderRadius:'6px', transition:'width 1s linear' }} />
                        </div>
                    </div>

                    {/* Mensagens do diretor */}
                    {mensagens.length > 0 && (
                        <div style={{ width:'100%', maxWidth:'800px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(251,146,60,0.3)', borderRadius:'20px', padding:'28px 36px' }}>
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                                    <span style={{ color:'#fb923c' }}><MicIcon /></span>
                                    <span style={{ fontSize:'20px', fontWeight:700, color:'#fff' }}>Mensagens do Diretor</span>
                                </div>
                                {mensagensNaoLidas.length > 0 && (
                                    <span style={{ padding:'4px 14px', borderRadius:'100px', background:'rgba(239,68,68,0.2)', border:'1px solid rgba(239,68,68,0.3)', color:'#f87171', fontSize:'14px', fontWeight:700 }}>
                                        {mensagensNaoLidas.length} ativa{mensagensNaoLidas.length !== 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                            <div style={{ display:'flex', flexDirection:'column', gap:'12px', maxHeight:'280px', overflowY:'auto' }}>
                                {mensagens.slice(0, 3).map(msg => (
                                    <div key={msg.id} style={{ background:'rgba(251,146,60,0.15)', border:'1px solid rgba(251,146,60,0.3)', borderRadius:'12px', padding:'16px 20px' }}>
                                        <p style={{ margin:'0 0 6px', fontSize:'20px', fontWeight:700, color:'#fb923c' }}>{msg.mensagem}</p>
                                        <p style={{ margin:0, fontSize:'13px', color:'rgba(251,146,60,0.6)' }}>
                                            {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' })}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // VISUALIZAÇÃO NORMAL
    const timerColor = tempoInfo?.atrasado ? '#f87171' : '#2CCB90';
    const timerBg = tempoInfo?.atrasado ? 'rgba(239,68,68,0.07)' : 'rgba(44,203,144,0.07)';
    const timerBorder = tempoInfo?.atrasado ? 'rgba(239,68,68,0.2)' : 'rgba(44,203,144,0.2)';

    return (
        <>
            <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }

                .ep-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border-radius:10px; font-size:13px; font-weight:600; border:1px solid #e2e8f0; background:#f8fafc; color:#64748b; cursor:pointer; font-family:inherit; transition:all 0.15s; }
                .ep-btn:hover { background:#f0fdf8; color:#1e293b; }
                .ep-btn.purple { background:rgba(124,58,237,0.08); border-color:rgba(124,58,237,0.2); color:#7c3aed; }
                .ep-btn.purple:hover { background:rgba(124,58,237,0.14); }

                .ep-menu-item { display:flex; align-items:center; gap:10px; width:100%; padding:10px 14px; border-radius:10px; font-size:13px; font-weight:500; color:#64748b; background:none; border:none; cursor:pointer; font-family:inherit; transition:all 0.15s; text-align:left; }
                .ep-menu-item:hover { background:#f8fafc; color:#1e293b; }
                .ep-menu-item.danger { color:#ef4444; }
                .ep-menu-item.danger:hover { background:rgba(239,68,68,0.06); }

                @media (max-width: 640px) { .ep-nav-email { display:none; } }
            `}</style>

            <div style={{ minHeight:'100vh', background:'#f0faf6', fontFamily:"'Inter', -apple-system, sans-serif" }}>

                {/* NAVBAR */}
                <header style={{ position:'sticky', top:0, zIndex:50, background:'rgba(255,255,255,0.9)', backdropFilter:'blur(24px)', WebkitBackdropFilter:'blur(24px)', borderBottom:'1px solid #e2e8f0' }}>
                    <div style={{ maxWidth:'1280px', margin:'0 auto', padding:'0 24px', height:'64px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'16px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'14px' }}>
                            <div style={{ display:'inline-flex', alignItems:'center', background:'#ffffff', borderRadius:'10px', padding:'6px 14px', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', border:'1px solid #e2e8f0' }}>
                                <img src="/logo-horizontal.png" alt="Evlo" style={{ height:'26px', objectFit:'contain' }} onError={e => { e.target.style.display='none'; }} />
                            </div>
                            <button onClick={() => navigate('/eventos')} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'7px 14px', borderRadius:'10px', background:'#f8fafc', border:'1px solid #e2e8f0', color:'#64748b', fontSize:'13px', fontWeight:500, cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', whiteSpace:'nowrap' }}
                                onMouseEnter={e => e.currentTarget.style.background='#f0fdf8'}
                                onMouseLeave={e => e.currentTarget.style.background='#f8fafc'}
                            >
                                <ArrowLeftIcon /> Voltar aos Eventos
                            </button>
                        </div>

                        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                            {eventoAtivo && tarefaAtual && (
                                <button className="ep-btn purple" onClick={toggleModoApresentacao}>
                                    <ExpandIcon /> Modo Apresentação
                                </button>
                            )}
                            {mensagensNaoLidas.length > 0 && (
                                <span style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'5px 12px', borderRadius:'100px', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', color:'#ef4444', fontSize:'12px', fontWeight:700 }}>
                                    <BellIcon /> {mensagensNaoLidas.length} nova{mensagensNaoLidas.length !== 1 ? 's' : ''}
                                </span>
                            )}

                            <div style={{ position:'relative' }}>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
                                    style={{ display:'flex', alignItems:'center', gap:'10px', padding:'6px 12px', borderRadius:'12px', background: menuOpen ? '#f8fafc' : '#ffffff', border:'1px solid #e2e8f0', cursor:'pointer', transition:'all 0.15s', fontFamily:'inherit' }}
                                >
                                    <div style={{ width:'32px', height:'32px', borderRadius:'50%', background:'linear-gradient(135deg, #2CCB90, #2DB8E1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px', fontWeight:700, color:'#fff', flexShrink:0 }}>{iniciais}</div>
                                    <div className="ep-nav-email"><p style={{ margin:0, fontSize:'13px', fontWeight:600, color:'#0f172a' }}>{nomeUsuario}</p></div>
                                    <span style={{ color:'#94a3b8', transition:'transform 0.2s', transform: menuOpen ? 'rotate(180deg)' : 'none' }}><ChevronIcon /></span>
                                </button>
                                {menuOpen && (
                                    <div style={{ position:'absolute', top:'calc(100% + 8px)', right:0, minWidth:'190px', background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:'14px', boxShadow:'0 16px 48px rgba(0,0,0,0.12)', padding:'8px', zIndex:100 }}>
                                        <button className="ep-menu-item" onClick={() => { navigate('/perfil'); setMenuOpen(false); }}>Meu Perfil</button>
                                        <div style={{ height:'1px', background:'#e2e8f0', margin:'6px 0' }} />
                                        <button className="ep-menu-item danger" onClick={handleLogout}><LogoutIcon /> Sair</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* CONTEÚDO */}
                <main style={{ position:'relative', zIndex:1, maxWidth:'900px', margin:'0 auto', padding:'32px 24px 80px' }}>

                    {/* Header */}
                    <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'28px' }}>
                        <div style={{ width:'40px', height:'40px', borderRadius:'12px', background:'linear-gradient(135deg, rgba(251,146,60,0.15), rgba(244,63,94,0.15))', border:'1px solid rgba(251,146,60,0.2)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fb923c' }}>
                            <MicIcon />
                        </div>
                        <div>
                            <h1 style={{ margin:0, fontSize:'clamp(20px,3vw,28px)', fontWeight:800, color:'#0f172a', letterSpacing:'-0.02em' }}>Visão do Palestrante</h1>
                            <p style={{ margin:0, fontSize:'13px', color:'#64748b' }}>
                                {eventoAtivo && tarefaAtual ? 'Sua apresentação está em andamento' : 'Aguardando início do evento'}
                            </p>
                        </div>
                    </div>

                    {eventoAtivo && tarefaAtual ? (
                        <>
                            {/* Cronômetro */}
                            <div style={{ background: timerBg, border:`2px solid ${timerBorder}`, borderRadius:'20px', padding:'32px', marginBottom:'20px' }}>
                                <div style={{ textAlign:'center', marginBottom:'24px' }}>
                                    <span style={{ display:'inline-flex', padding:'4px 16px', borderRadius:'100px', background: timerBg, border:`1px solid ${timerBorder}`, color: timerColor, fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'12px' }}>
                                        {tempoInfo?.atrasado ? 'Tempo Excedido' : 'No Prazo'}
                                    </span>
                                    <h2 style={{ margin:'0 0 4px', fontSize:'22px', fontWeight:700, color:'#0f172a' }}>{tarefaAtual.titulo}</h2>
                                    {tarefaAtual.responsavel && <p style={{ margin:0, fontSize:'14px', color:'#64748b' }}>Responsável: {tarefaAtual.responsavel}</p>}
                                </div>

                                {/* Timer display */}
                                <div style={{ background:'rgba(255,255,255,0.8)', border:'1px solid #e2e8f0', borderRadius:'16px', padding:'28px', textAlign:'center', marginBottom:'20px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', marginBottom:'8px' }}>
                                        <span style={{ color: timerColor }}><ClockIcon /></span>
                                        <span style={{ fontSize:'12px', color:'#94a3b8', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em' }}>
                                            Tempo {tempoInfo?.atrasado ? 'Extra' : 'Restante'}
                                        </span>
                                    </div>
                                    <div style={{ fontSize:'clamp(4rem, 12vw, 7rem)', fontWeight:800, color: timerColor, lineHeight:1, fontFamily:'monospace' }}>
                                        {formatarTempo(tempoInfo?.restante || 0)}
                                    </div>
                                    <p style={{ margin:'8px 0 0', fontSize:'13px', color:'#94a3b8' }}>
                                        Duração programada: {tarefaAtual.duracao} minutos
                                    </p>
                                </div>

                                {/* Progress */}
                                <div>
                                    <div style={{ height:'10px', borderRadius:'5px', background:'#e2e8f0', overflow:'hidden', marginBottom:'6px' }}>
                                        <div style={{ height:'100%', width:`${tempoInfo?.progresso || 0}%`, background: tempoInfo?.atrasado ? 'linear-gradient(90deg, #f87171, #ef4444)' : 'linear-gradient(90deg, #2CCB90, #2DB8E1)', borderRadius:'5px', transition:'width 1s linear' }} />
                                    </div>
                                    <p style={{ margin:0, textAlign:'center', fontSize:'12px', color:'#94a3b8' }}>
                                        {Math.round(tempoInfo?.progresso || 0)}% do tempo {tempoInfo?.atrasado ? 'excedido' : 'utilizado'}
                                    </p>
                                </div>

                                {/* Descrição */}
                                {tarefaAtual.descricao && (
                                    <div style={{ marginTop:'20px', background:'rgba(44,203,144,0.08)', border:'1px solid rgba(44,203,144,0.2)', borderRadius:'12px', padding:'14px 18px' }}>
                                        <p style={{ margin:'0 0 6px', fontSize:'12px', fontWeight:700, color:'#2CCB90', textTransform:'uppercase', letterSpacing:'0.06em' }}>Descrição da Apresentação</p>
                                        <p style={{ margin:0, fontSize:'14px', color:'#64748b', lineHeight:'1.6' }}>{tarefaAtual.descricao}</p>
                                    </div>
                                )}
                            </div>

                            {/* Mensagens do diretor */}
                            <div style={{ background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:'16px', padding:'20px 24px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
                                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                        <span style={{ color:'#fb923c' }}><MicIcon /></span>
                                        <span style={{ fontSize:'15px', fontWeight:700, color:'#0f172a' }}>Mensagens do Diretor</span>
                                    </div>
                                    {mensagensNaoLidas.length > 0 && (
                                        <span style={{ padding:'3px 10px', borderRadius:'100px', background:'rgba(251,146,60,0.15)', border:'1px solid rgba(251,146,60,0.3)', color:'#fb923c', fontSize:'11px', fontWeight:700 }}>
                                            {mensagensNaoLidas.length} ativa{mensagensNaoLidas.length !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                </div>

                                {mensagens.length === 0 ? (
                                    <div style={{ padding:'20px', textAlign:'center', borderRadius:'10px', background:'rgba(44,203,144,0.05)', border:'1px solid rgba(44,203,144,0.15)' }}>
                                        <p style={{ margin:'0 0 4px', fontSize:'14px', fontWeight:600, color:'#64748b' }}>Nenhuma mensagem ativa</p>
                                        <p style={{ margin:0, fontSize:'12px', color:'#94a3b8' }}>Mensagens do diretor aparecerão aqui quando ele ativá-las</p>
                                    </div>
                                ) : (
                                    <div style={{ display:'flex', flexDirection:'column', gap:'10px', maxHeight:'300px', overflowY:'auto' }}>
                                        {mensagens.map(msg => (
                                            <div key={msg.id} style={{ background:'rgba(251,146,60,0.12)', border:'1px solid rgba(251,146,60,0.25)', borderRadius:'12px', padding:'14px 18px' }}>
                                                <p style={{ margin:'0 0 4px', fontSize:'16px', fontWeight:700, color:'#fb923c' }}>{msg.mensagem}</p>
                                                <p style={{ margin:0, fontSize:'12px', color:'rgba(251,146,60,0.7)' }}>
                                                    {new Date(msg.created_at).toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit', second:'2-digit' })}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div style={{ background:'#ffffff', border:'1px solid #e2e8f0', borderRadius:'20px', padding:'60px', textAlign:'center', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                            <div style={{ width:'64px', height:'64px', borderRadius:'16px', background:'rgba(251,146,60,0.1)', border:'1px solid rgba(251,146,60,0.2)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px', color:'rgba(251,146,60,0.6)' }}>
                                <MicIcon />
                            </div>
                            <p style={{ margin:'0 0 8px', fontSize:'18px', fontWeight:700, color:'#64748b' }}>
                                {eventoAtivo ? 'Aguardando sua vez de apresentar' : 'Nenhum evento ativo no momento'}
                            </p>
                            <p style={{ margin:0, fontSize:'14px', color:'#94a3b8', lineHeight:'1.6' }}>
                                {eventoAtivo ? 'Você será notificado quando for sua vez' : 'Assim que um evento iniciar, você verá as informações aqui'}
                            </p>
                        </div>
                    )}
                </main>
            </div>
        </>
    );
};

export default EventosPalestrante;
